// JOBE-0167.2: MemoriesTabBody — orchestrator for the Memories tab.
//
// Owns filter state via useReducer; debounces filter changes (300ms) before
// refetch; loads via flaggedFetch('memory.mock.list') with real-fetch fallback
// to GET /api/memory; renders <MemoryFilterBar /> + <MemoryList />.
//
// The wire envelope field is `kind` (per JOBE-0076.1); the UI uses `type`
// per the vocabulary rule. The mapping happens at the request boundary
// (kind → type on response normalization; type → kind on request query).

import { useEffect, useReducer, useRef, useState, useCallback } from 'react';
import { useFlagStore } from '../../lib/useFlagStore.js';
import { flaggedFetch } from '../../lib/flags.js';
import { MemoryFilterBar, DEFAULT_FILTERS } from './MemoryFilterBar.jsx';
import { MemoryList } from './MemoryList.jsx';

// ---- filter reducer ----
function filterReducer(state, action) {
  if (action.type === 'patch') return { ...state, ...action.patch };
  if (action.type === 'reset') return { ...DEFAULT_FILTERS };
  return state;
}

// Map a wire memory envelope (kind) into the UI shape (type).
export function normalizeMemory(m) {
  if (!m) return m;
  const out = { ...m };
  if (m.type === undefined && m.kind !== undefined) {
    out.type = m.kind;
  }
  return out;
}

// Build the GET /api/memory query string. UI `type` → wire `kind`.
export function buildMemoryQuery(filters, cursor) {
  const p = new URLSearchParams();
  if (filters.subject) p.set('subject', filters.subject);
  if (filters.type) p.set('kind', filters.type);
  if (filters.lifecycle) p.set('lifecycle', filters.lifecycle);
  if (filters.owner) p.set('owner', filters.owner);
  if (filters.tier) p.set('tier', filters.tier);
  if (filters.windowFrom) p.set('window_from', filters.windowFrom);
  if (filters.windowTo) p.set('window_to', filters.windowTo);
  if (cursor) p.set('cursor', cursor);
  p.set('limit', '20');
  return `/api/memory?${p.toString()}`;
}

// Debounce delay for filter→refetch. Exported for tests.
export const DEBOUNCE_MS = 300;

/**
 * @param {object} [props]
 * @param {(urn: string) => void} [props.onExpand]
 *    Parent-supplied callback (wired by JOBE-0167.3 to open the inspect view).
 */
export function MemoriesTabBody({ onExpand }) {
  const [filters, dispatch] = useReducer(filterReducer, DEFAULT_FILTERS, (init) => ({ ...init }));
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceTimer = useRef(null);

  // Debounce filter -> debouncedFilters.
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [filters]);

  const overrides = useFlagStore((s) => s.overrides);
  const [state, setState] = useState('loading');
  const [memories, setMemories] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const loadMore = useCallback(async (cursor) => {
    const url = buildMemoryQuery(debouncedFilters, cursor);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`memory list ${resp.status}`);
      const json = await resp.json();
      const rows = (json.memories || []).map(normalizeMemory);
      setMemories((prev) => [...prev, ...rows]);
      setNextCursor(json.nextCursor || null);
      if (json.totalCount != null) setTotalCount(json.totalCount);
    } catch {
      setState('error');
    }
  }, [debouncedFilters]);

  // Initial / refetch.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      setState('loading');
      // Fixture path.
      const fx = flaggedFetch('memory.mock.list', overrides);
      if (fx !== undefined) {
        const rows = (Array.isArray(fx) ? fx : (fx.memories || [])).map(normalizeMemory);
        const filtered = applyFiltersClientSide(rows, debouncedFilters);
        if (cancelled) return;
        setMemories(filtered);
        setTotalCount(filtered.length);
        setNextCursor(Array.isArray(fx) ? null : (fx.nextCursor || null));
        setState('loaded');
        return;
      }
      // Real path.
      const url = buildMemoryQuery(debouncedFilters);
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`memory list ${resp.status}`);
        const json = await resp.json();
        if (cancelled) return;
        const rows = (json.memories || []).map(normalizeMemory);
        setMemories(rows);
        setTotalCount(json.totalCount != null ? json.totalCount : rows.length);
        setNextCursor(json.nextCursor || null);
        setState('loaded');
      } catch {
        if (cancelled) return;
        setState('error');
      }
    }
    run();
    return () => { cancelled = true; };
    // overrides is referentially stable per zustand; debouncedFilters drives refetch.
    // retryNonce included so Retry forces a re-run.
  }, [debouncedFilters, overrides, retryNonce]);

  const handleChange = useCallback((patch) => {
    dispatch({ type: 'patch', patch });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  const handleRetry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (nextCursor) loadMore(nextCursor);
  }, [nextCursor, loadMore]);

  const handleExpand = useCallback((urn) => {
    if (typeof onExpand === 'function') onExpand(urn);
  }, [onExpand]);

  return (
    <div className="memories-tab-body" data-testid="memories-tab-body">
      <MemoryFilterBar
        filters={filters}
        onChange={handleChange}
        onReset={handleReset}
      />
      <MemoryList
        state={state}
        memories={memories}
        totalCount={totalCount}
        nextCursor={nextCursor}
        onReset={handleReset}
        onRetry={handleRetry}
        onLoadMore={handleLoadMore}
        onExpand={handleExpand}
      />
    </div>
  );
}

// Apply filters client-side against the fixture (the fixture endpoint doesn't
// honor query params). Window filtering is intentionally light — checks
// `valid_from` lies within [windowFrom, windowTo].
function applyFiltersClientSide(rows, f) {
  return rows.filter((m) => {
    if (f.subject && m.subject && !m.subject.includes(f.subject)) return false;
    if (f.type && m.type !== f.type) return false;
    if (f.lifecycle && m.lifecycle !== f.lifecycle) return false;
    if (f.owner && m.owner && !m.owner.includes(f.owner)) return false;
    if (f.tier && m.tier && m.tier !== f.tier) return false;
    if (f.windowFrom && m.valid_from && m.valid_from.slice(0, 10) < f.windowFrom) return false;
    if (f.windowTo && m.valid_from && m.valid_from.slice(0, 10) > f.windowTo) return false;
    return true;
  });
}

export default MemoriesTabBody;
